import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, Search as SearchIcon, FolderOpen, User, LogOut, Menu, ScanLine, Moon, Sun, Lock, Mail, Plus } from 'lucide-react';
import '../css/DynamicComponentLoader.css';
import '../css/login.css';

import Search from "./search";
import Library from "./library";
import Collections from "./collections";
import Profile from "./profile";

const DynamicComponentLoader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [password3, setPassword3] = useState('');
  const [email, setEmail] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [loggedInUsername, setLoggedInUsername] = useState('');
  const [isForgotPassword, setIsForgetPassword] = useState(false);
  const [isForgotPasswordSent, setIsForgetPasswordSent] = useState(false);
  const [isForgetPasswordFlow, setIsForgetPasswordFlow] = useState(false);
  const [tokenIsValid, setTokenIsValid] = useState(false);
  const [token, setToken] = useState(false);
  const [passwordNotMatch, setPasswordNotMatch] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [inviteToken, setInviteToken] = useState('');
  const [inviteStatusMessage, setInviteStatusMessage] = useState('');
  const [inviteErrorMessage, setInviteErrorMessage] = useState('');
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const handleComponentChange = (path) => {
    if(sidebarOpen){
      setSidebarOpen(false);
    }
    navigate(path);
  };

  const toggleLoginForm = () => {
    setShowLoginForm(!showLoginForm);
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = isLogin ? "/api/auth/login" : "/api/auth/register";
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          password,
          email,
          invite_token: isLogin ? undefined : inviteToken
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        if (data.access_token) {
          localStorage.setItem('isLoggedIn', true);
          localStorage.setItem('accessToken', data.access_token);
          localStorage.setItem('loggedInUsername', data.username);

          setAccessToken(data.access_token);
          setLoggedInUsername(data.username);
          setIsLoggedIn(true);
          setShowLoginForm(false);
          setInviteStatusMessage('');
          setInviteErrorMessage('');
          handleComponentChange('/library');
        } else if (response.status === 201) {
          setIsLogin(true);
          setIsLoggedIn(false);
          setShowLoginForm(true);
        }
      } else {
        setInviteErrorMessage(data?.error || 'Login or registration failed');
      }
    } catch (error) {
      console.error('Error:', error);
      setInviteErrorMessage('Unable to contact server');
    }
    setUsername('');
    setPassword('');
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setSidebarOpen(false);
    setInviteStatusMessage('');
    setInviteErrorMessage('');
    // Remove items from local storage on logout
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('loggedInUsername');
    setAccessToken(null);
    setLoggedInUsername('');
    setShowLoginForm(true);
    navigate('/login', { replace: true });
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    try {
      const response = fetch("/api/auth/reset_password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });
      setEmail('');
      setIsForgetPassword(false);
      setIsForgetPasswordSent(true);
  
    } catch (error) {
      console.error('Error:', error);
    }
  }

  const checkInviteIsValid = async (inviteValue) => {
    if (!inviteValue) {
      return;
    }

    try {
      const response = await fetch('/api/auth/check_invite_valid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: inviteValue })
      });

      if (response.ok) {
        setInviteStatusMessage('Invite link detected. Complete registration within 5 minutes.');
        setInviteErrorMessage('');
      } else {
        const data = await response.json().catch(() => ({}));
        setInviteStatusMessage('');
        setInviteErrorMessage(data?.error || 'Invite link is invalid or expired');
      }
    } catch (error) {
      setInviteStatusMessage('');
      setInviteErrorMessage('Unable to validate invite link');
    }
  };

  const handlePasswordReset = async (password, confirm) => {
    if(password !== confirm){
      setPasswordNotMatch(true);
    }else{
      try {
        const response = await fetch("/api/auth/reset_with_token", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ token, password }),
        });
        if (response.ok) {
          const data = await response.json();
          setTokenIsValid(false);
          setIsForgetPassword(false);
          setIsForgetPasswordFlow(false);
          setIsLogin(true);

          const newUrl = window.location.protocol + "//" + window.location.host;
          window.history.replaceState({path: newUrl}, '', newUrl);

        } else {
          console.error('Password Reset Failure');
        }
      } catch (error) {
        console.error('Error:', error);
      }  
    }
  }

  const checkTokenIsValid = async (token) => {
    try {
      const response = await fetch("/api/auth/check_token_valid", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token }),
      });
      if (response.ok) {
        const data = await response.json();
        if(data.valid){
          setTokenIsValid(true);
          setToken(token)
          console.log("VALID")
        }else{
          setTokenIsValid(false);
        }
      } else {
        console.error('Login or registration failed');
      }
    } catch (error) {
      console.error('Error:', error);
    }

  }

  function extractTokenFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('passwordreset');
  }

  function extractInviteFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('invite');
  }

  useEffect(() => {
    const token = extractTokenFromUrl();
    const inviteFromUrl = extractInviteFromUrl();

    if (inviteFromUrl) {
      setIsLogin(false);
      setShowLoginForm(true);
      setInviteToken(inviteFromUrl);
      checkInviteIsValid(inviteFromUrl);
      if (location.pathname !== '/login') {
        navigate('/login', { replace: true });
      }
    }

    if(token != null){
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('loggedInUsername');
      setAccessToken(null);
      setLoggedInUsername('');
      setIsForgetPasswordFlow(true);
      checkTokenIsValid(token);
    }

    const loggedInStatus = localStorage.getItem('isLoggedIn');
    if (loggedInStatus === 'true') {
      setIsLoggedIn(true);
      const savedAccessToken = localStorage.getItem('accessToken');
      const savedUsername = localStorage.getItem('loggedInUsername');
      if (savedAccessToken && savedUsername) {
        setShowLoginForm(false);
        setAccessToken(savedAccessToken);
        setLoggedInUsername(savedUsername);
      }
    } else {
      setSidebarOpen(false);
      setShowLoginForm(true);
    }
    setAuthChecked(true);
  }, []);

  useEffect(() => {
    if (!authChecked) {
      return;
    }
    if (!isLoggedIn) {
      setShowLoginForm(true);
      if (location.pathname !== '/login') {
        navigate('/login', { replace: true });
      }
      return;
    }

    if (location.pathname === '/' || location.pathname === '/login') {
      navigate('/library', { replace: true });
    }
  }, [authChecked, isLoggedIn, location.pathname, navigate]);

  const handlePasswordResetSent = () => {
    setIsForgetPasswordSent(false); setIsLogin(true);
  }
  
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  useEffect(() => {
    const modalOpen = Boolean(
      showLoginForm ||
      isForgotPassword ||
      isForgetPasswordFlow ||
      isForgotPasswordSent ||
      passwordNotMatch
    );
    document.body.classList.toggle('modal-open', modalOpen);
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [
    showLoginForm,
    isForgotPassword,
    isForgetPasswordFlow,
    isForgotPasswordSent,
    passwordNotMatch
  ]);


  return (
    <div className="page-container">
      {/* Mobile toggle */}
      {isLoggedIn && (
        <button className="sidebar-mobile-toggle" onClick={toggleSidebar}>
          <Menu size={20} />
        </button>
      )}

      {/* Sidebar */}
      {isLoggedIn && (
        <>
          <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={closeSidebar}></div>
          <aside className={`app-sidebar ${sidebarOpen ? 'open' : ''}`}>
            <div className="sidebar-brand">M</div>
            <nav className="sidebar-nav">
              <button
                className={`sidebar-nav-btn ${location.pathname === '/library' ? 'active' : ''}`}
                onClick={() => handleComponentChange('/library')}
                title="Library"
              >
                <BookOpen size={20} />
              </button>
              <button
                className="sidebar-nav-btn"
                onClick={() => { closeSidebar(); navigate('/library', { state: { add: true } }); }}
                title="Add Book"
              >
                <Plus size={20} />
              </button>
              <button
                className={`sidebar-nav-btn ${location.pathname === '/search' ? 'active' : ''}`}
                onClick={() => handleComponentChange('/search')}
                title="Search"
              >
                <SearchIcon size={20} />
              </button>
              <button
                className={`sidebar-nav-btn ${location.pathname === '/collections' ? 'active' : ''}`}
                onClick={() => handleComponentChange('/collections')}
                title="Collections"
              >
                <FolderOpen size={20} />
              </button>
              <button
                className={`sidebar-nav-btn ${location.pathname === '/profile' ? 'active' : ''}`}
                onClick={() => handleComponentChange('/profile')}
                title={loggedInUsername}
              >
                <User size={20} />
              </button>
            </nav>
            <div className="sidebar-bottom">
              <button
                className="sidebar-nav-btn sidebar-theme-btn"
                onClick={() => {
                  const next = theme === 'dark' ? 'light' : 'dark';
                  setTheme(next);
                  localStorage.setItem('theme', next);
                }}
                title="Toggle theme"
              >
                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              <button className="sidebar-logout-btn" onClick={handleLogout} title="Logout">
                <LogOut size={20} />
              </button>
            </div>
          </aside>
        </>
      )}

      <main className="app-main">
        <Routes>
          <Route path="/" element={<Navigate to={isLoggedIn ? "/library" : "/login"} replace />} />
          <Route path="/login" element={isLoggedIn ? <Navigate to="/library" replace /> : null} />
          <Route path="/library" element={authChecked && isLoggedIn ? <Library /> : null} />
          <Route path="/search" element={authChecked && isLoggedIn ? <Search /> : null} />
          <Route path="/collections" element={authChecked && isLoggedIn ? <Collections /> : null} />
          <Route path="/profile" element={authChecked && isLoggedIn ? <Profile /> : null} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* Mobile Theme Toggle */}
      {isLoggedIn && (
        <button
          className="mobile-theme-toggle"
          onClick={() => { const next = theme === 'dark' ? 'light' : 'dark'; setTheme(next); localStorage.setItem('theme', next); }}
          aria-label="Toggle theme"
        >
          {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
        </button>
      )}

      {/* Mobile Bottom Tab Bar */}
      {isLoggedIn && (
        <nav className="mobile-tab-bar">
          <button
            className={`mobile-tab ${location.pathname === '/library' ? 'active' : ''}`}
            onClick={() => handleComponentChange('/library')}
          >
            <BookOpen size={20} />
            <span>Library</span>
          </button>
          <button
            className="mobile-tab"
            onClick={() => { navigate('/library', { state: { add: true } }); }}
          >
            <Plus size={20} />
            <span>Add</span>
          </button>
          <button
            className={`mobile-tab ${location.pathname === '/search' ? 'active' : ''}`}
            onClick={() => handleComponentChange('/search')}
          >
            <SearchIcon size={20} />
            <span>Search</span>
          </button>
          <button
            className={`mobile-tab ${location.pathname === '/collections' ? 'active' : ''}`}
            onClick={() => handleComponentChange('/collections')}
          >
            <FolderOpen size={20} />
            <span>Collect</span>
          </button>
          <button
            className={`mobile-tab ${location.pathname === '/profile' ? 'active' : ''}`}
            onClick={() => handleComponentChange('/profile')}
          >
            <User size={20} />
            <span>Profile</span>
          </button>
        </nav>
      )}

      {showLoginForm && !isLoggedIn && !isForgotPassword && !isForgetPasswordFlow && !isForgotPasswordSent && (
        <div className="login-backdrop">
          <button className="login-theme-toggle" onClick={() => { const next = theme === 'light' ? 'dark' : 'light'; setTheme(next); localStorage.setItem('theme', next); }} aria-label="Toggle theme">
            {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
          </button>
          <div className="login-card">
            <div className="login-card-header">
              <div className="login-brand-mark">M</div>
              <h1 className="login-brand-name">Manderley Library</h1>
              <p className="login-brand-subtitle">{isLogin ? 'Sign in to your account' : 'Create a new account'}</p>
            </div>
            <form className="login-card-body" onSubmit={handleSubmit}>
              <div>
                <label className="login-field-label">Username</label>
                <input className="login-input" placeholder="Enter your username" type="text" value={username} onChange={(e) => setUsername(e.target.value)} />
              </div>
              <div>
                <label className="login-field-label">Password</label>
                <input className="login-input" placeholder="Enter your password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              {!isLogin && (
                <div>
                  <label className="login-field-label">Email</label>
                  <input className="login-input" placeholder="Enter your email" type="text" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
              )}
              <button className="login-btn-primary" type="submit">{isLogin ? 'Sign In' : 'Create Account'}</button>
              {inviteStatusMessage && <div className="login-message login-message-info">{inviteStatusMessage}</div>}
              {inviteErrorMessage && <div className="login-message login-message-error">{inviteErrorMessage}</div>}
              <div className="login-divider"><span>or</span></div>
              <button className="login-btn-secondary" type="button" onClick={toggleMode}>
                {isLogin ? "Don't have an account? Register" : 'Already have an account? Sign In'}
              </button>
              {isLogin && <button className="login-forgot-link" type="button" onClick={() => setIsForgetPassword(true)}>Forgot your password?</button>}
            </form>
          </div>
        </div>
      )}

      {isForgotPassword && !isForgetPasswordFlow && (
        <div className="login-backdrop">
          <button className="login-theme-toggle" onClick={() => { const next = theme === 'light' ? 'dark' : 'light'; setTheme(next); localStorage.setItem('theme', next); }} aria-label="Toggle theme">
            {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
          </button>
          <div className="login-card">
            <div className="login-card-header">
              <div className="login-brand-mark">M</div>
              <h1 className="login-brand-name">Manderley Library</h1>
              <p className="login-brand-subtitle">Reset your password</p>
            </div>
            <form className="login-card-body" onSubmit={handleForgotPassword}>
              <div>
                <label className="login-field-label">Email</label>
                <input className="login-input" placeholder="Enter your email" type="text" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <button className="login-btn-primary" type="submit">Request Reset Link</button>
              <button className="login-btn-secondary" type="button" onClick={() => setIsForgetPassword(false)}>Back to Sign In</button>
            </form>
          </div>
        </div>
      )}

      {isForgotPasswordSent && (
        <div className="login-backdrop">
          <div className="login-card">
            <div className="login-card-header">
              <div className="login-brand-mark">M</div>
              <h1 className="login-brand-name">Manderley Library</h1>
              <p className="login-brand-subtitle">Check your inbox</p>
            </div>
            <form className="login-card-body" onSubmit={handlePasswordResetSent}>
              <div className="login-message login-message-info">You should receive a link to reset your password within 5 minutes.</div>
              <button className="login-btn-primary" type="submit">Back to Sign In</button>
            </form>
          </div>
        </div>
      )}

      {isForgetPasswordFlow && !tokenIsValid && (
        <div className="login-backdrop">
          <div className="login-card">
            <div className="login-card-header">
              <div className="login-brand-mark">M</div>
              <h1 className="login-brand-name">Manderley Library</h1>
              <p className="login-brand-subtitle">Invalid token</p>
            </div>
            <div className="login-card-body">
              <div className="login-message login-message-error">This reset link is no longer valid. Please request a new one.</div>
            </div>
          </div>
        </div>
      )}

      {isForgetPasswordFlow && tokenIsValid && !isLoggedIn && (
        <div className="login-backdrop">
          <div className="login-card">
            <div className="login-card-header">
              <div className="login-brand-mark">M</div>
              <h1 className="login-brand-name">Manderley Library</h1>
              <p className="login-brand-subtitle">Set a new password</p>
            </div>
            <div className="login-card-body">
              <div>
                <label className="login-field-label">New Password</label>
                <input className="login-input" placeholder="Enter new password" type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} />
              </div>
              <div>
                <label className="login-field-label">Confirm Password</label>
                <input className="login-input" placeholder="Confirm new password" type="password" value={password3} onChange={(e) => setPassword3(e.target.value)} />
              </div>
              <button className="login-btn-primary" type="button" onClick={() => handlePasswordReset(password2, password3)}>Reset Password</button>
              <button className="login-btn-secondary" type="button" onClick={() => {setIsForgetPasswordFlow(false); setIsLogin(true)}}>Back to Sign In</button>
            </div>
          </div>
        </div>
      )}

      {passwordNotMatch && (
        <div className="login-backdrop">
          <div className="login-card">
            <div className="login-card-header">
              <div className="login-brand-mark">M</div>
              <h1 className="login-brand-name">Manderley Library</h1>
              <p className="login-brand-subtitle">Password mismatch</p>
            </div>
            <div className="login-card-body">
              <div className="login-message login-message-error">The passwords you entered do not match. Please try again.</div>
              <button className="login-btn-primary" type="button" onClick={() => {setPasswordNotMatch(false); setPassword2(''); setPassword3('');}}>Try Again</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicComponentLoader;
